import { useEffect, useRef } from "react";

import { useToast } from "../components/ToastHost";
import { ANY_ENTITY } from "./tabFreshness.mjs";
import { useTabUpdatedAtState } from "./tabFreshnessData";
import { useTenantId } from "./tenant";

/**
 * Data freshness signal for the dashboard shell (the sidebar badge).
 *
 * AJ 2026-09-30: the badge reads public.sync_log, the same record as the
 * per-tab "Updated @" chip, through the same cached read
 * (tabFreshnessData.ts). The shell passes the entity behind the route on
 * screen (tabFreshness.mjs entityForTabRoute), so the badge and the chip
 * print the same instant. A route with no entity of its own (Settings,
 * Insights, DOT Audit) falls back to the tenant's newest successful sync of
 * any entity. Before this it probed max(last_updated_date) on audit_finding
 * and financial_health, a second clock that could disagree with the chip.
 *
 * 2026-10-05: for an entity fed by source files the stamp is the earlier of
 * that sync and the newest loaded file (tabFreshness.mjs combineSourceStamp),
 * so a refresh that re-pushed old data no longer reads as fresh.
 *
 * MER-152 part 1+2. Threshold defaults:
 *   - fresh:   last update within 24h
 *   - recent:  24h to 7d
 *   - stale:   7d to 14d
 *   - severe:  > 14d
 *
 * Customer connectors fire on the 4h scheduler (MER-151) which means
 * a healthy DWS should never go past 24h between updates. The
 * thresholds are conservative to give signal early.
 */

export type FreshnessLevel = "fresh" | "recent" | "stale" | "severe" | "unknown";

export interface Freshness {
  level: FreshnessLevel;
  lastUpdated: Date | null;
  ageHours: number | null;
  /** The sync_log entity the stamp came from (or the incident snapshot key). */
  entity: string | null;
  /** True when the stamp is a calendar date with no time (Incidents). */
  dateOnly: boolean;
  isLoading: boolean;
  error: string | null;
}

const HOUR_MS = 60 * 60 * 1000;

function levelForAge(ageHours: number | null): FreshnessLevel {
  if (ageHours === null) return "unknown";
  if (ageHours < 24) return "fresh";
  if (ageHours < 24 * 7) return "recent";
  if (ageHours < 24 * 14) return "stale";
  return "severe";
}

/**
 * The freshness of `entityKey` (the route's entity; null or undefined means
 * "any entity") for the current tenant, from sync_log.
 */
export function useFreshness(entityKey?: string | null): Freshness {
  // MER-167: fire a toast once when freshness transitions into the
  // severe band. lastLevelRef prevents re-firing on subsequent renders.
  const { push } = useToast();
  const lastLevelRef = useRef<FreshnessLevel | null>(null);
  const tenantId = useTenantId();
  const { pending, stamp } = useTabUpdatedAtState(tenantId, entityKey ?? ANY_ENTITY);

  const lastUpdated = stamp?.at ?? null;
  const ageHours = lastUpdated ? (Date.now() - lastUpdated.getTime()) / HOUR_MS : null;
  const level = levelForAge(ageHours);

  useEffect(() => {
    if (!lastUpdated) return;
    // MER-167: fire a sticky warning toast once when freshness enters the
    // severe band (>14d). Dedup via the ToastProvider so reload doesn't
    // replay it; lastLevelRef catches mid-session transitions.
    if (level === "severe" && lastLevelRef.current !== "severe") {
      push({
        type: "warning",
        title: "Data severely stale",
        body: `Last refresh was over 14 days ago. Trigger a sync or check the upload portal.`,
        sticky: true,
        dedupeKey: "freshness-severe",
      });
    }
    lastLevelRef.current = level;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, lastUpdated?.getTime()]);

  return {
    level,
    lastUpdated,
    ageHours,
    entity: stamp?.entity ?? null,
    dateOnly: stamp?.dateOnly ?? false,
    isLoading: !tenantId || pending,
    error: null,
  };
}

const LEVEL_COLOR: Record<FreshnessLevel, { bg: string; fg: string; label: string }> = {
  fresh: {
    bg: "rgba(39, 174, 96, 0.10)",
    fg: "rgb(20, 130, 70)",
    label: "Fresh",
  },
  recent: {
    bg: "rgba(38, 168, 159, 0.10)",
    fg: "var(--brand-deep)",
    label: "Recent",
  },
  stale: {
    bg: "rgba(230, 126, 34, 0.12)",
    fg: "rgb(180, 90, 20)",
    label: "Stale",
  },
  severe: {
    bg: "rgba(192, 57, 43, 0.12)",
    fg: "var(--flag-red)",
    label: "Severely stale",
  },
  unknown: {
    bg: "rgba(149, 165, 166, 0.12)",
    fg: "var(--text-muted)",
    label: "Unknown",
  },
};

export function freshnessBadge(level: FreshnessLevel): { bg: string; fg: string; label: string } {
  return LEVEL_COLOR[level];
}

export function formatRelativeAge(ageHours: number | null): string {
  if (ageHours === null) return "unknown";
  if (ageHours < 1) return `${Math.round(ageHours * 60)}m ago`;
  if (ageHours < 24) return `${Math.round(ageHours)}h ago`;
  const days = Math.floor(ageHours / 24);
  return `${days}d ago`;
}

export function formatAsOf(lastUpdated: Date | null, dateOnly = false): string {
  if (!lastUpdated) return "Awaiting first refresh";
  if (dateOnly) {
    return lastUpdated.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  return lastUpdated.toLocaleString(undefined, opts);
}
